import { DOCUMENT } from '@angular/common';
import { Injectable, effect, signal, computed, EffectRef, afterNextRender, inject } from '@angular/core';

import { ENVIRONMENT } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';

import { SdkJob } from './models/tracking-event';

const USHERU_TRACKING_API_ENDPOINT_DEMO = 'https://uts-dev.usheru.com';
const USHERU_TRACKING_API_ENDPOINT_PROD = 'https://uts.usheru.com';

@Injectable({ providedIn: 'root' })
export class TrackingUsheruService {
  private readonly channelService = inject(ChannelService);
  private readonly env = inject(ENVIRONMENT);
  private readonly document = inject(DOCUMENT);

  private readonly sdk = signal<UsheruSDK | null>(null);
  private readonly jobQueue = signal<SdkJob[]>([]);
  /** true once the SDK is set the first time */
  private readonly sdkReady = computed(() => !!this.sdk());
  private readonly sdkReadyOnceRef: EffectRef;

  constructor() {
    /*
     * Runs whenever **either** sdk() or jobQueue() changes.
     * Because we empty the queue after flushing, the effect
     * only stays alive while there are still events to deliver.
     */
    this.sdkReadyOnceRef = effect(
      () => {
        /* Wait until sdk exists */
        if (!this.sdkReady()) return;

        const queue = this.jobQueue();
        if (queue.length === 0) return;

        for (const job of queue) {
          this.execute(job);
        }

        // stop listening to the effect once queue drained.
        this.sdkReadyOnceRef.destroy();
      },
      { manualCleanup: true }
    );

    afterNextRender(() => {
      this.initializeSdk();
    });
  }

  track(job: SdkJob): void {
    /*
     *  1. If SDK is ready → call immediately.
     *  2. Otherwise → push into queue (immutable update).
     */
    if (this.sdkReady()) {
      this.execute(job);
    } else {
      this.jobQueue.update(q => [...q, job]);
    }
  }

  /* ---------- internal dispatcher ---------- */
  private execute(job: SdkJob): void {
    const sdk = this.sdk()!;
    switch (job.kind) {
      case 'usheru_tag':
        // eslint-disable-next-line no-case-declarations
        const category = job.properties['category'].toString();
        sdk.usheru_tag(category, job.action, job.properties);
        break;

      case 'intersection':
        sdk.addIntersectionObserver(job.eventType, job.eventName, job.querySelector, job.payload, job.visibilityRatio);
        break;
    }
  }

  private async initializeSdk(): Promise<UsheruSDK> {
    if (this.sdk()) {
      return Promise.resolve(this.sdk());
    }
    try {
      const UsheruTrackingSDK: typeof UsheruSDK = await this.getUsheruTrackingSDKClass();
      const sdkConfig: UsheruSDKConfigInterface = {
        baseUrl: this.env.mode === 'prod' ? USHERU_TRACKING_API_ENDPOINT_PROD : USHERU_TRACKING_API_ENDPOINT_DEMO,
        uuidChannel: this.channelService.channel.uuid,
        channelName: this.channelService.channel.name
      };
      const sdk = await UsheruTrackingSDK.create(sdkConfig);

      this.sdk.set(sdk);
      return sdk;
    } catch (e) {
      console.error('Failed to load UsheruTrackingSDK', e);
      Promise.resolve();
    }
  }

  private getUsheruTrackingSDKClass(): Promise<any> {
    return new Promise((resolve, reject) => {
      const trackingUrl = `https://cdnstatic.usheru.com/usheru-tracking-sdk/v2/usheru-tracking-sdk.es.js`;
      const script = this.document.createElement('script');
      script.setAttribute('async', '');
      script.src = trackingUrl;
      script.type = 'module'; // If the script is an ES module
      script.onload = () => {
        resolve(window['UsheruTrackingSDK']); // Access the module after it loads
      };
      script.onerror = error => {
        reject(error);
      };
      this.document.head.appendChild(script);
    });
  }
}

// you can find more functionality in this documentation https://github.com/UsheruHQ/usheru-tracking-sdk
class UsheruSDK {
  static create: (config: UsheruSDKConfigInterface) => UsheruSDK;
  constructor() {}
  usheru_tag: (eventType: string, eventName: string, payload: { [key: string]: string | string[] | number }) => void;
  addEventListener: (
    eventType: string,
    eventName: string,
    querySelector: string,
    payload: { [key: string]: string | string[] | number }
  ) => void;
  addIntersectionObserver: (
    eventType: string,
    eventName: string,
    querySelector: string,
    payload: { [key: string]: string | string[] | number },
    visibilityRatio?: number
  ) => void;
}

interface UsheruSDKConfigInterface {
  baseUrl: string;
  uuidChannel: string;
  channelName: string;
  authToken?: string | null;
  sessionExpirationMinutes?: number;
  eventService?: unknown | null;
  storageService?: unknown | null;
  debug?: boolean;
}
